import React from "react"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import Link from "next/link"
import { Image, ResponsiveImageType } from "react-datocms"
import Slider from "react-slick"
import colors from "@constants/colors"
import { body, bodyBold, smallcaps, h6 } from "@constants/typography"
import { StyledLink } from "@components/buttons/LinkButton"

interface FeaturedCard {
  id?: string
  title?: string
  subtitle?: string
  image?: ResponsiveImageType
  imageSrc?: string
  imageAlt?: string
  ctaText?: string
  ctaLink?: string
  titleColor?: string
  subtitleColor?: string
  buttonTextColor?: string
  buttonBackgroundColor?: string
}

interface FeaturedCardWithButtonProps {
  title?: string
  backgroundColor?: string
  backgroundImageSrc?: string
  ctaText?: string
  ctaLink?: string
  ctaTextColor?: string
  ctaBackgroundColor?: string
  featuredCards?: FeaturedCard[]
}

const Section = styled.section<{ bgColor?: string; bgImage?: string }>`
  ${({ bgColor, bgImage }) => css`
    width: 100%;
    background-color: ${bgColor ? `#${bgColor}` : "#3c3d40"};
    background-image: ${bgImage
      ? `linear-gradient(rgba(0, 0, 0, 0.28), rgba(0, 0, 0, 0.28)), url(${bgImage})`
      : "none"};
    background-size: cover;
    background-position: center;
  `}
`

const Container = styled.div`
  max-width: 1220px;
  margin: 0 auto;
  padding: 20px 12px 28px;

  ${up("laptop")} {
    padding: 18px 20px 32px;
  }
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
`

const SectionTitle = styled.h2`
  ${({ color }) => css`
    ${h6}
    margin: 0;
    color: ${color || colors.snow};
    font-size: 28px;
    line-height: 34px;

    ${up("laptop")} {
      font-size: 32px;
      line-height: 38px;
    }
  `}
`

const SectionCta = styled(StyledLink)`
  ${css`
    text-transform: uppercase;
    letter-spacing: 1.5px;
    font-size: 12px;
    height: 36px;
    padding: 0 15px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.22);
    gap: 12px;
    transition: opacity 160ms ease;

    &:hover,
    &:focus-visible {
      opacity: 0.7;
    }
  `}
`

const CardsGrid = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: 1fr;

  ${up("laptop")} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
`

const CardsSlider = styled(Slider)`
  position: relative;

  .slick-list {
    overflow: hidden;
  }

  .slick-track {
    display: flex;
  }

  .slick-slide > div {
    padding: 0 8px;
  }

  .slick-prev,
  .slick-next {
    display: flex !important;
    align-items: center;
    justify-content: center;
    z-index: 1;
    transform: translateY(-50%);
    top: 140px;
    width: 42px;
    height: 42px;
    border-radius: 50px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
  }

  .slick-prev {
    left: 8px;
  }

  .slick-next {
    right: 8px;
  }

  .slick-prev:before,
  .slick-next:before {
    content: "";
  }

  .slick-prev:after,
  .slick-next:after {
    content: "";
    width: 12px;
    height: 12px;
    border-right: 3px solid #e11a22;
    border-bottom: 3px solid #e11a22;
  }

  .slick-prev:after {
    transform: rotate(135deg);
  }

  .slick-next:after {
    transform: rotate(-45deg);
  }

  ${up("laptop")} {
    .slick-prev,
    .slick-next {
      top: 150px;
    }
  }
`

const Card = styled.article`
  display: flex;
  flex-direction: column;
`

const CardImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 1.3 / 1;
  border-radius: 14px;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.22);

  .DatoImage {
    width: 100%;
    height: 100%;
  }

  .DatoImage img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 14px 8px 0;

  ${up("laptop")} {
    padding: 16px 10px 0;
  }
`

const CardTitle = styled.h3<{ color?: string }>`
  ${({ color }) => css`
    ${bodyBold}
    margin: 0;
    color: ${color ? `#${color}` : colors.snow};
    text-transform: uppercase;
    letter-spacing: 0.9px;
    text-align: center;
    font-size: 19px;
    line-height: 24px;
  `}
`

const CardSubtitle = styled.p<{ color?: string }>`
  ${({ color }) => css`
    ${body}
    margin: 0;
    color: ${color ? `#${color}` : colors.snow};
    opacity: 0.85;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    text-align: center;
    font-size: 14px;
    line-height: 19px;
  `}
`

const CardButton = styled(StyledLink)`
  ${css`
    width: auto;
    align-self: center;
    min-height: 30px;
    margin-top: 8px;
    padding: 6px 34px;
    border-radius: 999px;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    font-size: 12px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    font-weight: 600;
    transition: opacity 160ms ease;

    &:hover,
    &:focus-visible {
      opacity: 0.7;
    }
  `}
`

const Arrow = styled.span`
  font-size: 16px;
`

const FeaturedCardWithButton: React.FC<FeaturedCardWithButtonProps> = ({
  title,
  backgroundColor,
  backgroundImageSrc,
  ctaText,
  ctaLink,
  ctaTextColor,
  ctaBackgroundColor,
  featuredCards = [],
}) => {
  const shouldUseSlider = featuredCards.length > 3
  const cardItems = featuredCards.map((card, index) => {
    const key = card.id || `${card.title || "card"}-${index}`
    const cardLink = card.ctaLink
    const cardCtaText = card.ctaText || "Learn More"

    return (
      <Card key={key}>
        <CardImageWrapper>
          {card.image ? (
            <Image data={card.image} className="DatoImage" />
          ) : card.imageSrc ? (
            <CardImage src={card.imageSrc} alt={card.imageAlt || ""} />
          ) : null}
        </CardImageWrapper>

        <CardBody>
          {card.title && (
            <CardTitle color={card.titleColor}>{card.title}</CardTitle>
          )}
          {card.subtitle && (
            <CardSubtitle color={card.subtitleColor}>
              {card.subtitle}
            </CardSubtitle>
          )}
          {cardLink && (
            <Link href={cardLink} passHref prefetch={false}>
              <CardButton
                $buttonColor={card.buttonBackgroundColor || ctaBackgroundColor}
                $buttonTextColor={card.buttonTextColor || ctaTextColor}
              >
                {cardCtaText}
              </CardButton>
            </Link>
          )}
        </CardBody>
      </Card>
    )
  })

  return (
    <Section bgColor={backgroundColor} bgImage={backgroundImageSrc}>
      <Container>
        <HeaderRow>
          <SectionTitle>{title}</SectionTitle>
          {ctaText && ctaLink && (
            <Link href={ctaLink} passHref prefetch={false}>
              <SectionCta
                $buttonColor={ctaBackgroundColor}
                $buttonTextColor={ctaTextColor}
              >
                {ctaText}
                <Arrow>&rsaquo;</Arrow>
              </SectionCta>
            </Link>
          )}
        </HeaderRow>

        {shouldUseSlider ? (
          <CardsSlider
            dots={false}
            arrows={true}
            infinite={featuredCards.length > 3}
            speed={400}
            slidesToShow={3}
            slidesToScroll={1}
            responsive={[
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 2,
                },
              },
              {
                breakpoint: 640,
                settings: {
                  slidesToShow: 1,
                },
              },
            ]}
          >
            {cardItems}
          </CardsSlider>
        ) : (
          <CardsGrid>{cardItems}</CardsGrid>
        )}
      </Container>
    </Section>
  )
}

export default FeaturedCardWithButton
